import React, { useState, useMemo, useEffect } from 'react'
import { Button, Form, Badge, Row, Col, InputGroup, Alert } from 'react-bootstrap'
import { CheckSquare, ChevronDown, ChevronUp, Search, Square } from 'react-bootstrap-icons'

const PermissionSelector = ({ componentData, selectedPermissions, onPermissionChange }) => {
    const [expandedCategories, setExpandedCategories] = useState({})
    const [searchTerm, setSearchTerm] = useState('')
    const [validationErrors, setValidationErrors] = useState({})

    // Action display mapping
    const actionDisplayMap = {
        'get': 'View Access',
        'add': 'Create Access',
        'update': 'Edit Access',
        'delete': 'Delete Access'
    }

    // Function to format action text for display
    const formatActionText = (action) => {
        return actionDisplayMap[action] || action;
    }

    // Toggle category expansion
    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }))
    }

    // Expand or collapse all categories
    const toggleAllCategories = (expand) => {
        const newState = {}
        componentData.forEach(category => {
            newState[category.category] = expand
        })
        setExpandedCategories(newState)
    }

    // Toggle all permissions in a category
    const toggleAllInCategory = (category, components) => {
        const allPermissionsInCategory = components.flatMap(comp => 
            comp.actions.map(action => `${comp.name}:${action}`)
        )
        
        const allSelected = allPermissionsInCategory.every(perm => 
            selectedPermissions.includes(perm)
        )

        let newPermissions = [...selectedPermissions]
        
        if (allSelected) {
            // Remove all permissions from this category
            newPermissions = newPermissions.filter(
                perm => !allPermissionsInCategory.includes(perm)
            )
        } else {
            // Add missing permissions from this category
            const missingPermissions = allPermissionsInCategory.filter(
                perm => !newPermissions.includes(perm)
            )
            newPermissions = [...newPermissions, ...missingPermissions]
        }

        onPermissionChange(newPermissions)
    }

    // Check if a permission requires "get" permission
    const requiresGetPermission = (component, action) => {
        // Only require get permission if the component has multiple actions
        // and this is one of the dependent actions
        const componentActions = componentData
            .flatMap(cat => cat.components)
            .find(comp => comp.name === component)?.actions || [];
        
        // If component only has "add" action, don't require get permission
        if (componentActions.length === 1 && componentActions[0] === 'add') {
            return false;
        }
        
        // For components with multiple actions, require get for add/update/delete
        return ['add', 'update', 'delete'].includes(action);
    }

    // Validate permissions
    const validatePermissions = (permissions) => {
        const errors = {};
        
        // Group permissions by component
        const componentPermissions = {};
        permissions.forEach(permission => {
            const [component, action] = permission.split(':');
            if (!componentPermissions[component]) {
                componentPermissions[component] = [];
            }
            componentPermissions[component].push(action);
        });
        
        // Check for missing get permissions only for components that require it
        Object.entries(componentPermissions).forEach(([component, actions]) => {
            const componentInfo = componentData
                .flatMap(cat => cat.components)
                .find(comp => comp.name === component);
                
            // Skip validation for components that only have "add" action
            if (componentInfo && componentInfo.actions.length === 1 && componentInfo.actions[0] === 'add') {
                return;
            }
            
            const hasGetPermission = actions.includes('get');
            const hasOtherPermissions = actions.some(action => 
                ['add', 'update', 'delete'].includes(action)
            );
            
            if (hasOtherPermissions && !hasGetPermission) {
                errors[component] = `"View Access" permission is required for ${component}`;
            }
        });
        
        return errors;
    }

    // Toggle individual permission
    const handlePermissionToggle = (component, action) => {
        const permission = `${component}:${action}`
        let newPermissions = [...selectedPermissions]
        
        if (newPermissions.includes(permission)) {
            newPermissions = newPermissions.filter(p => p !== permission)
        } else {
            newPermissions.push(permission)
            
            // If adding add/update/delete, automatically add get if required and not present
            if (requiresGetPermission(component, action) && !newPermissions.includes(`${component}:get`)) {
                newPermissions.push(`${component}:get`)
            }
        }
        
        onPermissionChange(newPermissions)
    }

    // Filter components based on search term with highlighting
    const filteredData = useMemo(() => {
        if (!componentData) return []
        
        return componentData.map(category => ({
            ...category,
            components: category.components
                .map(comp => ({
                    ...comp,
                    // Highlight matching text
                    displayName: comp.name,
                    // Filter actions if search term matches action names
                    actions: comp.actions.filter(action => 
                        searchTerm === '' || 
                        comp.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                        action.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                }))
                .filter(comp => 
                    searchTerm === '' || 
                    comp.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                    comp.actions.length > 0
                )
        })).filter(category => category.components.length > 0)
    }, [componentData, searchTerm])

    // Count selected permissions for each category
    const getCategorySelectionCount = (category) => {
        const allPermissionsInCategory = category.components.flatMap(comp => 
            comp.actions.map(action => `${comp.name}:${action}`)
        )
        return allPermissionsInCategory.filter(perm => 
            selectedPermissions.includes(perm)
        ).length
    }

    // Validate permissions whenever selectedPermissions changes
    useEffect(() => {
        const errors = validatePermissions(selectedPermissions);
        setValidationErrors(errors);
    }, [selectedPermissions]);

    return (
        <div className="permission-selector">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Select Permissions</h5>
                <div>
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => toggleAllCategories(true)}
                    >
                        Expand All
                    </Button>
                    <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => toggleAllCategories(false)}
                    >
                        Collapse All
                    </Button>
                </div>
            </div>

            <div className="mb-3">
                <InputGroup>
                    <InputGroup.Text>
                        <Search />
                    </InputGroup.Text>
                    <Form.Control
                        type="text"
                        placeholder="Search components or actions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setSearchTerm('')}
                        >
                            Clear
                        </Button>
                    )}
                </InputGroup>
            </div>
            
            {/* Display validation errors */}
            {Object.keys(validationErrors).length > 0 && (
                <Alert variant="warning" className="mt-3">
                    <strong>Permission Validation Issues:</strong>
                    <ul className="mb-0 mt-2">
                        {Object.values(validationErrors).map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </Alert>
            )}
            
            <div className="mt-3 mb-2 p-2 bg-light rounded">
                <div className="d-flex justify-content-between">
                    <span>Total Selected Permissions:</span>
                    <strong>{selectedPermissions.length}</strong>
                </div>
            </div>

            <div className="accordion" id="permissionsAccordion">
                {filteredData?.map((category, catIndex) => {
                    const selectionCount = getCategorySelectionCount(category)
                    const totalPermissions = category.components.reduce(
                        (total, comp) => total + comp.actions.length, 0
                    )
                    
                    return (
                        <div key={category.category} className="accordion-item">
                            <h2 className="accordion-header">
                                <button
                                    className="accordion-button d-flex justify-content-between align-items-center"
                                    type="button"
                                    onClick={() => toggleCategory(category.category)}
                                    aria-expanded={expandedCategories[category.category] || false}
                                >
                                    <div className="d-flex align-items-center">
                                        <span className="fw-semibold me-2">{category.category}</span>
                                        <Badge bg={selectionCount === totalPermissions ? "success" : "primary"}>
                                            {selectionCount}/{totalPermissions}
                                        </Badge>
                                    </div>
                                </button>
                            </h2>
                            <div 
                                className={`accordion-collapse collapse ${expandedCategories[category.category] ? 'show' : ''}`}
                            >
                                <div className="accordion-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted">
                                            {selectionCount === totalPermissions 
                                                ? "All permissions selected" 
                                                : `${selectionCount} of ${totalPermissions} selected`
                                            }
                                        </span>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0"
                                            onClick={() => toggleAllInCategory(category.category, category.components)}
                                        >
                                            {selectionCount === totalPermissions ? (
                                                <><CheckSquare className="me-1" /> Deselect All</>
                                            ) : (
                                                <><Square className="me-1" /> Select All</>
                                            )}
                                        </Button>
                                    </div>
                                    
                                    {category?.components?.map((component) => {
                                        // Check if this component only has "add" action
                                        const hasOnlyAddAction = component.actions.length === 1 && component.actions[0] === 'add';
                                        
                                        return (
                                            <div key={component?.name} className="mb-3 p-2 border rounded">
                                                <h6 className="fw-semibold mb-2 d-flex align-items-center">
                                                    <span>{component.name}</span>
                                                    <Badge bg="light" text="dark" className="ms-2">
                                                        {component.actions.length} actions
                                                    </Badge>
                                                    {validationErrors[component.name] && (
                                                        <Badge bg="warning" text="dark" className="ms-2">
                                                            Requires View Access
                                                        </Badge>
                                                    )}
                                                    {hasOnlyAddAction && (
                                                        // <Badge bg="info" text="dark" className="ms-2">
                                                        //     Standalone Create Access
                                                        // </Badge>
                                                        <div></div>
                                                    )}
                                                </h6>
                                                <Row>
                                                    {component?.actions?.map((action) => {
                                                        const permissionKey = `${component.name}:${action}`
                                                        const isSelected = selectedPermissions.includes(permissionKey)
                                                        const requiresGet = requiresGetPermission(component.name, action)
                                                        const hasGetPermission = selectedPermissions.includes(`${component.name}:get`)
                                                        const isDisabled = requiresGet && !hasGetPermission && !isSelected
                                                        
                                                        return (
                                                            <Col xs={6} md={4} lg={3} key={permissionKey}>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    id={permissionKey}
                                                                    label={formatActionText(action)}
                                                                    checked={isSelected}
                                                                    onChange={() => handlePermissionToggle(component.name, action)}
                                                                    className="mb-2"
                                                                    disabled={isDisabled}
                                                                />
                                                                {isDisabled && (
                                                                    <div className="small text-muted">
                                                                        Requires "View Access"
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        )
                                                    })}
                                                </Row>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
            
            {filteredData?.length === 0 && (
                <div className="text-center p-4 text-muted">
                    <Search size={24} className="mb-2" />
                    <p>No permissions found matching "{searchTerm}"</p>
                </div>
            )}
        </div>
    )
}

export default PermissionSelector



//Previous Code
// import React, { useState, useMemo } from 'react'
// import { Button, Form, Badge, Row, Col, InputGroup } from 'react-bootstrap'
// import { CheckSquare, ChevronDown, ChevronUp, Search, Square } from 'react-bootstrap-icons'
// // import { FiSearch, FiChevronDown, FiChevronUp, FiCheckSquare, FiSquare } from 'react-icons/fi'

// const PermissionSelector = ({ componentData, selectedPermissions, onPermissionChange }) => {
//     const [expandedCategories, setExpandedCategories] = useState({})
//     const [searchTerm, setSearchTerm] = useState('')

//     // Toggle category expansion
//     const toggleCategory = (category) => {
//         setExpandedCategories(prev => ({
//             ...prev,
//             [category]: !prev[category]
//         }))
//     }

//     // Expand or collapse all categories
//     const toggleAllCategories = (expand) => {
//         const newState = {}
//         componentData.forEach(category => {
//             newState[category.category] = expand
//         })
//         setExpandedCategories(newState)
//     }

//     // Toggle all permissions in a category
//     const toggleAllInCategory = (category, components) => {
//         const allPermissionsInCategory = components.flatMap(comp => 
//             comp.actions.map(action => `${comp.name}:${action}`)
//         )
        
//         const allSelected = allPermissionsInCategory.every(perm => 
//             selectedPermissions.includes(perm)
//         )

//         let newPermissions = [...selectedPermissions]
        
//         if (allSelected) {
//             // Remove all permissions from this category
//             newPermissions = newPermissions.filter(
//                 perm => !allPermissionsInCategory.includes(perm)
//             )
//         } else {
//             // Add missing permissions from this category
//             const missingPermissions = allPermissionsInCategory.filter(
//                 perm => !newPermissions.includes(perm)
//             )
//             newPermissions = [...newPermissions, ...missingPermissions]
//         }

//         onPermissionChange(newPermissions)
//     }

//     // Toggle individual permission
//     const handlePermissionToggle = (component, action) => {
//         const permission = `${component}:${action}`
//         let newPermissions = [...selectedPermissions]
        
//         if (newPermissions.includes(permission)) {
//             newPermissions = newPermissions.filter(p => p !== permission)
//         } else {
//             newPermissions.push(permission)
//         }
        
//         onPermissionChange(newPermissions)
//     }

//     // Filter components based on search term with highlighting
//     const filteredData = useMemo(() => {
//         if (!componentData) return []
        
//         return componentData.map(category => ({
//             ...category,
//             components: category.components
//                 .map(comp => ({
//                     ...comp,
//                     // Highlight matching text
//                     displayName: comp.name,
//                     // Filter actions if search term matches action names
//                     actions: comp.actions.filter(action => 
//                         searchTerm === '' || 
//                         comp.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
//                         action.toLowerCase().includes(searchTerm.toLowerCase())
//                     )
//                 }))
//                 .filter(comp => 
//                     searchTerm === '' || 
//                     comp.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
//                     comp.actions.length > 0
//                 )
//         })).filter(category => category.components.length > 0)
//     }, [componentData, searchTerm])

//     // Count selected permissions for each category
//     const getCategorySelectionCount = (category) => {
//         const allPermissionsInCategory = category.components.flatMap(comp => 
//             comp.actions.map(action => `${comp.name}:${action}`)
//         )
//         return allPermissionsInCategory.filter(perm => 
//             selectedPermissions.includes(perm)
//         ).length
//     }

//     return (
//         <div className="permission-selector">
//             <div className="d-flex justify-content-between align-items-center mb-3">
//                 <h5 className="mb-0">Select Permissions</h5>
//                 <div>
//                     <Button 
//                         variant="outline-primary" 
//                         size="sm" 
//                         className="me-2"
//                         onClick={() => toggleAllCategories(true)}
//                     >
//                         Expand All
//                     </Button>
//                     <Button 
//                         variant="outline-primary" 
//                         size="sm"
//                         onClick={() => toggleAllCategories(false)}
//                     >
//                         Collapse All
//                     </Button>
//                 </div>
//             </div>

//             <div className="mb-3">
//                 <InputGroup>
//                     <InputGroup.Text>
//                         <Search />
//                     </InputGroup.Text>
//                     <Form.Control
//                         type="text"
//                         placeholder="Search components or actions..."
//                         value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                     />
//                     {searchTerm && (
//                         <Button 
//                             variant="outline-secondary" 
//                             onClick={() => setSearchTerm('')}
//                         >
//                             Clear
//                         </Button>
//                     )}
//                 </InputGroup>
//             </div>
//             <div className="mt-3 mb-2 p-2 bg-light rounded">
//                 <div className="d-flex justify-content-between">
//                     <span>Total Selected Permissions:</span>
//                     <strong>{selectedPermissions.length}</strong>
//                 </div>
//             </div>

//             <div className="accordion" id="permissionsAccordion">
//                 {filteredData?.map((category, catIndex) => {
//                     const selectionCount = getCategorySelectionCount(category)
//                     const totalPermissions = category.components.reduce(
//                         (total, comp) => total + comp.actions.length, 0
//                     )
                    
//                     return (
//                         <div key={category.category} className="accordion-item">
//                             <h2 className="accordion-header">
//                                 <button
//                                     className="accordion-button d-flex justify-content-between align-items-center"
//                                     type="button"
//                                     onClick={() => toggleCategory(category.category)}
//                                     aria-expanded={expandedCategories[category.category] || false}
//                                 >
//                                     <div className="d-flex align-items-center">
//                                         <span className="fw-semibold me-2">{category.category}</span>
//                                         <Badge bg={selectionCount === totalPermissions ? "success" : "primary"}>
//                                             {selectionCount}/{totalPermissions}
//                                         </Badge>
//                                     </div>
//                                     {/* <div>
//                                         {expandedCategories[category.category] ? (
//                                             <ChevronUp className="ms-2" />       
//                                         ) : (
//                                             <ChevronDown className="ms-2" />
//                                         )}
//                                     </div> */}
//                                 </button>
//                             </h2>
//                             <div 
//                                 className={`accordion-collapse collapse ${expandedCategories[category.category] ? 'show' : ''}`}
//                             >
//                                 <div className="accordion-body p-3">
//                                     <div className="d-flex justify-content-between align-items-center mb-2">
//                                         <span className="text-muted">
//                                             {selectionCount === totalPermissions 
//                                                 ? "All permissions selected" 
//                                                 : `${selectionCount} of ${totalPermissions} selected`
//                                             }
//                                         </span>
//                                         <Button
//                                             variant="link"
//                                             size="sm"
//                                             className="p-0"
//                                             onClick={() => toggleAllInCategory(category.category, category.components)}
//                                         >
//                                             {selectionCount === totalPermissions ? (
//                                                 <><CheckSquare className="me-1" /> Deselect All</>
//                                             ) : (
//                                                 <><Square className="me-1" /> Select All</>
//                                             )}
//                                         </Button>
//                                     </div>
                                    
//                                     {category?.components?.map((component) => (
//                                         <div key={component?.name} className="mb-3 p-2 border rounded">
//                                             <h6 className="fw-semibold mb-2 d-flex align-items-center">
//                                                 <span>{component.name}</span>
//                                                 <Badge bg="light" text="dark" className="ms-2">
//                                                     {component.actions.length} actions
//                                                 </Badge>
//                                             </h6>
//                                             <Row>
//                                                 {component.actions.map((action) => {
//                                                     const permissionKey = `${component.name}:${action}`
//                                                     const isSelected = selectedPermissions.includes(permissionKey)
                                                    
//                                                     return (
//                                                         <Col xs={6} md={4} lg={3} key={permissionKey}>
//                                                             <Form.Check
//                                                                 type="checkbox"
//                                                                 id={permissionKey}
//                                                                 label={action}
//                                                                 checked={isSelected}
//                                                                 onChange={() => handlePermissionToggle(component.name, action)}
//                                                                 className="mb-2"
//                                                             />
//                                                         </Col>
//                                                     )
//                                                 })}
//                                             </Row>
//                                         </div>
//                                     ))}
//                                 </div>
//                             </div>
//                         </div>
//                     )
//                 })}
//             </div>
            
//             {filteredData.length === 0 && (
//                 <div className="text-center p-4 text-muted">
//                     <Search size={24} className="mb-2" />
//                     <p>No permissions found matching "{searchTerm}"</p>
//                 </div>
//             )}
            
//             {/* <div className="mt-3 p-2 bg-light rounded">
//                 <div className="d-flex justify-content-between">
//                     <span>Total Selected Permissions:</span>
//                     <strong>{selectedPermissions.length}</strong>
//                 </div>
//             </div> */}
//         </div>
//     )
// }

// export default PermissionSelector